export interface QualityCondition {
  metric: string;
  operator: string;
  value: number | null;
}

export const INCENTIVE_VERSION_V1 = 'v1';
export const INCENTIVE_VERSION_V2 = 'v2';

export function isLogisticsV2Version(version: string | undefined | null): boolean {
  return version === INCENTIVE_VERSION_V2;
}

export function resolveIncentiveVersionFromApi(incentive: { incentiveVersion?: string } | null | undefined): string {
  return isLogisticsV2Version(incentive && incentive.incentiveVersion)
    ? INCENTIVE_VERSION_V2
    : INCENTIVE_VERSION_V1;
}

const RATE_METRICS = new Set(['apr', 'dpr']);
const DAILY_PING_METRICS = new Set(['rider_reject', 'rider_busy', 'rider_abort']);

/** Ping metrics allowed in daily qualityConditions[] (matches captain-incentive v1 scope). */
export const DAILY_QUALITY_CONDITION_METRICS = [
  { label: 'Rider Reject', value: 'rider_reject' },
  { label: 'Rider Busy', value: 'rider_busy' },
  { label: 'Rider Abort', value: 'rider_abort' },
];

/** @deprecated use DAILY_QUALITY_CONDITION_METRICS for daily v2 UI */
export const QUALITY_CONDITION_METRICS = DAILY_QUALITY_CONDITION_METRICS;

export const DAILY_QUALITY_SLAB_CONDITION_OPTIONS = [
  { label: 'None (slab only)', value: '' },
  { label: 'Strict AND (&&)', value: '&&' },
];

/** @deprecated use DAILY_QUALITY_SLAB_CONDITION_OPTIONS */
export const QUALITY_SLAB_CONDITION_OPTIONS = DAILY_QUALITY_SLAB_CONDITION_OPTIONS;

export const DAILY_QUALITY_SLAB_HELP_TEXT =
  'Strict AND (&&): SI and QI pay together only when the order/distance slab and all ping quality conditions are met; otherwise ₹0 on that calc run. Metrics: Rider Reject, Rider Busy, Rider Abort.';

/** @deprecated use DAILY_QUALITY_SLAB_HELP_TEXT */
export const QUALITY_SLAB_HELP_TEXT = DAILY_QUALITY_SLAB_HELP_TEXT;

export function isDailyPingQualityMetric(metric: string): boolean {
  return DAILY_PING_METRICS.has((metric || '').toLowerCase());
}

export function defaultOperatorForMetric(metric: string): string {
  const normalized = (metric || '').toLowerCase();
  if (RATE_METRICS.has(normalized)) {
    return 'gte';
  }
  if (DAILY_PING_METRICS.has(normalized)) {
    return 'lte';
  }
  return 'gte';
}

/** Human-readable operator label for the daily quality UI. */
export function formatQualityOperator(operator: string): string {
  const op = (operator || '').toLowerCase();
  switch (op) {
    case 'lte':
      return '≤ lte';
    case 'gte':
      return '≥ gte';
    case 'lt':
      return '< lt';
    case 'gt':
      return '> gt';
    case 'eq':
      return '= eq';
    default:
      return op;
  }
}

export function emptyQualityCondition(): QualityCondition {
  return { metric: 'rider_reject', operator: 'lte', value: null };
}

export function isV2QualityRule(rule: any): boolean {
  return !!(rule && rule.qualityConditions && rule.qualityConditions.length);
}

export function isLegacyQualityRule(rule: any): boolean {
  return !!(rule && rule.quality && rule.quality.metric);
}

export function attachDailyV2Quality(ruleObj: any): void {
  ruleObj.quality = { amount: null };
  ruleObj.qualityConditions = [emptyQualityCondition()];
}

export function attachLegacyWeeklyQuality(ruleObj: any, metric: string | null = null): void {
  ruleObj.quality = {
    metric: metric,
    value: null,
    amount: null,
  };
}

export function attachLegacyQualityFromSlab(rule: any, slab: any): void {
  const metric = (slab.quality_metric || slab.qualityMetric || '').toLowerCase();
  const value = slab.quality_value != null ? slab.quality_value : slab.qualityValue;
  const amount = slab.quality_amount != null ? slab.quality_amount : slab.qualityAmount;
  if (!metric || value == null || amount == null) {
    return;
  }
  rule.quality = {
    metric,
    value,
    amount,
  };
}

export function filterQualityConditions(conditions: QualityCondition[]): QualityCondition[] {
  return (conditions || []).filter(c => {
    const metric = (c.metric || '').toLowerCase();
    return metric !== 'rider_cancel' && isDailyPingQualityMetric(metric);
  });
}

export function normalizeQualityConditionsFromApi(conditions: QualityCondition[]): QualityCondition[] {
  return filterQualityConditions(conditions || []).map((condition: QualityCondition) => ({
    metric: (condition.metric || '').toLowerCase(),
    operator: condition.operator || defaultOperatorForMetric(condition.metric),
    value: condition.value,
  }));
}

/** True when rule has at least one ping quality condition after API normalization. */
export function hasDailyPingQualityRule(rule: any): boolean {
  return isV2QualityRule(rule)
    && normalizeQualityConditionsFromApi(rule.qualityConditions).length > 0;
}

/** V2 QI amount: empty / omitted → 0; slab quality gate still applies when conditions exist. */
export function coalesceV2QualityAmount(amount: number | string | null | undefined): number {
  if (amount == null || amount === '') {
    return 0;
  }
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sanitizeV2RuleForApi(rule: any): any {
  const sanitized = { ...rule };
  if (isV2QualityRule(sanitized)) {
    sanitized.quality = {
      amount: coalesceV2QualityAmount(sanitized.quality ? sanitized.quality.amount : null),
    };
    sanitized.qualityConditions = normalizeQualityConditionsFromApi(sanitized.qualityConditions);
  } else if (sanitized.quality) {
    delete sanitized.qualityConditions;
  } else {
    delete sanitized.quality;
    delete sanitized.qualityConditions;
  }
  return sanitized;
}

/** @deprecated use sanitizeV2RuleForApi */
export const sanitizeDailyRuleForApi = sanitizeV2RuleForApi;

export function formatQualityConditionLabel(condition: QualityCondition): string {
  const metric = DAILY_QUALITY_CONDITION_METRICS.find(m => m.value === (condition.metric || '').toLowerCase());
  const metricLabel = metric ? metric.label : (condition.metric || '').toUpperCase();
  const op = condition.operator === 'gte' ? '>=' : '<=';
  return `${metricLabel} ${op} ${condition.value}`;
}

export function shouldEmitQualitySlabCondition(value: string): boolean {
  return value === '&&';
}

export function attachV2QualityFromSlab(rule: any, slab: any): void {
  const metric = (slab.quality_metric || slab.qualityMetric || '').toLowerCase();
  const value = slab.quality_value != null ? slab.quality_value : slab.qualityValue;
  const amount = slab.quality_amount != null ? slab.quality_amount : slab.qualityAmount;
  if (!isDailyPingQualityMetric(metric) || value == null || amount == null) {
    return;
  }
  rule.quality = { amount };
  rule.qualityConditions = [{
    metric,
    operator: defaultOperatorForMetric(metric),
    value,
  }];
}
